<script lang="ts" setup>
import { computed, ref, toRef, watch } from 'vue'
import BaseModal from '@/components/BaseModal.vue'
import { IFactura } from '@shared/types'
import { EStatusFactura } from '@shared/types/enums'
import { getStorage, ref as storageRef, uploadBytes } from 'firebase/storage'
import { apiService } from '@/services/apiService'
const errorMessages = ref<string>('')
const showErrorModal = ref(false)
const showSuccessModal = ref(false)
const successMessage = ref<string>('')
const storage = getStorage()
const props = defineProps<{
	idInstitution: string
	institutionName: string
}>()
const idInstitution = toRef(props, 'idInstitution')
const factura = ref<IFactura>({
	title: '',
	idInstitution: idInstitution.value,
	institutionName: props.institutionName,
	dateFactura: Date.now(),
	status: EStatusFactura.PENDIENTE,
	pdfPath: '',
	xmlPath: ''
})

const pdfFiles = ref<File[]>([])
const xmlFiles = ref<File[]>([])
const complementFiles = ref<File[]>([])

const pdfFileName = computed(() => pdfFiles.value?.[0]?.name ?? '')
const xmlFileName = computed(() => xmlFiles.value?.[0]?.name ?? '')
const complementFileName = computed(() => complementFiles.value?.[0]?.name ?? '')

const isPdf = (file?: File) => {
	if (!file) return false
	return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
}
const isXml = (file?: File) => {
	if (!file) return false
	return file.type === 'text/xml' || file.type === 'application/xml' || file.name.toLowerCase().endsWith('.xml')
}

const validateFiles = () => {
	const pdf = pdfFiles.value
	const xml = xmlFiles.value
	const complement = complementFiles.value

	if (pdf.length > 0 && !isPdf(pdf[0])) {
		errorMessages.value = 'El archivo de Factura (PDF) debe ser un PDF.'
		showErrorModal.value = true
		pdfFiles.value = []
		return
	}
	if (xml.length > 0 && !isXml(xml[0])) {
		errorMessages.value = 'El archivo de Factura (XML) debe ser un XML.'
		showErrorModal.value = true
		xmlFiles.value = []
		return
	}
	if (complement.length > 0 && !isPdf(complement[0])) {
		errorMessages.value = 'El archivo de Complemento debe ser un PDF.'
		showErrorModal.value = true
		complementFiles.value = []
	}
}

watch([pdfFiles, xmlFiles, complementFiles], validateFiles, { deep: true })
const uploadFile = async (file: File, defaultName: string, path: string, folder: string) => {
	const safeName = (file.name || defaultName).replace(/[\\/]/g, '_').replace(/\.\./g, '_').trim()
	const fileName = `${Date.now()}_${safeName}`
	const storagePath = storageRef(storage, `${path}/${folder}/${fileName}`)
	await uploadBytes(storagePath, file)
	return storagePath.fullPath.toString()
}
const validateForm = () => {
	if (!factura.value.title.trim()) {
		showErrorModal.value = true
		errorMessages.value = 'El título es requerido'
		return false
	}
	if (!pdfFiles.value) {
		showErrorModal.value = true
		errorMessages.value = 'El archivo de factura (PDF) es requerido'
		return false
	}
	if (!xmlFiles.value) {
		showErrorModal.value = true
		errorMessages.value = 'El archivo de factura (XML) es requerido'
		return false
	}
	return true
}
const submitForm = async () => {
	if (!validateForm()) {
		return
	}
	try {
		if (pdfFiles.value.length > 0) {
			factura.value.pdfPath = await uploadFile(pdfFiles.value[0], 'factura', `files/institutions/${props.idInstitution}/facturas`, factura.value.title)
		}
		if (xmlFiles.value.length > 0) {
			factura.value.xmlPath = await uploadFile(xmlFiles.value[0], 'factura', `files/institutions/${props.idInstitution}/facturas`, factura.value.title)
		}
		if (complementFiles.value.length > 0) {
			factura.value.complementPath = await uploadFile(complementFiles.value[0], 'complemento', `files/institutions/${props.idInstitution}/facturas`, factura.value.title)
		}
		const invoicePayload = {
			title: factura.value.title,
			idInstitution: props.idInstitution,
			institutionName: props.institutionName,
			dateFactura: factura.value.dateFactura,
			status: factura.value.status,
			pdfPath: factura.value.pdfPath,
			xmlPath: factura.value.xmlPath,
			...(factura.value.complementPath != null && { complementPath: factura.value.complementPath })
		}
		await apiService
			.post('createInvoiceNode/crear', {
				Invoice: invoicePayload
			})
			.then(() => {
				successMessage.value = 'Factura registrada correctamente'
				showSuccessModal.value = true
			})
			.catch((error: any) => {
				showErrorModal.value = true
				errorMessages.value = error.message || 'Error al registrar la factura. Verifica la consola para más detalles.'
			})
			.finally(() => {
				cleanForm()
			})
	} catch (error) {
		showErrorModal.value = true
		errorMessages.value = (error as Error).message
	}
}
const cleanForm = () => {
	factura.value = {
		title: '',
		idInstitution: props.idInstitution,
		institutionName: props.institutionName,
		dateFactura: Date.now(),
		pdfPath: '',
		xmlPath: '',
		complementPath: undefined,
		status: EStatusFactura.PENDIENTE
	}
	pdfFiles.value = []
	xmlFiles.value = []
	complementFiles.value = []
}
</script>
<template>
	<VaCard class="registro-facturas-card">
		<VaCardTitle class="header-gradient">
			<div class="header-content">
				<div class="header-main">
					<div class="header-icon-wrapper">
						<VaIcon name="receipt_long" class="header-icon" />
					</div>
					<div class="header-text">
						<h2 class="header-title">Registro de facturas</h2>
						<p class="header-subtitle">Carga los documentos requeridos: PDF, XML y Complemento (PDF)</p>
					</div>
				</div>
			</div>
		</VaCardTitle>

		<VaCardContent class="card-content">
			<div class="form-grid">
				<VaInput v-model="factura.title" label="Folio interno" placeholder="Ej: Factura Enero 2026" class="form-field" />
			</div>

			<div class="upload-grid">
				<div class="upload-section">
					<div class="upload-section-header">
						<VaIcon name="picture_as_pdf" class="upload-section-icon" />
						<div class="upload-section-text">
							<h4 class="upload-section-title">Factura (PDF)</h4>
							<span class="upload-section-hint">Solo PDF</span>
						</div>
					</div>
					<div class="upload-section-content">
						<VaFileUpload
							v-model="pdfFiles"
							dropzone
							color="primary"
							fileTypes=".pdf,application/pdf"
							uploadButtonText="Seleccionar PDF"
							dropZoneText="Arrastra tu PDF aquí o haz clic para seleccionar"
							class="upload-control" />
						<div v-if="pdfFileName" class="selected-file">
							<VaIcon name="check_circle" color="success" size="small" />
							<span class="selected-file__name">{{ pdfFileName }}</span>
						</div>
					</div>
				</div>

				<div class="upload-section">
					<div class="upload-section-header">
						<VaIcon name="code" class="upload-section-icon" />
						<div class="upload-section-text">
							<h4 class="upload-section-title">Factura (XML)</h4>
							<span class="upload-section-hint">Solo XML</span>
						</div>
					</div>
					<div class="upload-section-content">
						<VaFileUpload
							v-model="xmlFiles"
							dropzone
							color="success"
							fileTypes=".xml,application/xml,text/xml"
							uploadButtonText="Seleccionar XML"
							dropZoneText="Arrastra tu XML aquí o haz clic para seleccionar"
							class="upload-control" />
						<div v-if="xmlFileName" class="selected-file">
							<VaIcon name="check_circle" color="success" size="small" />
							<span class="selected-file__name">{{ xmlFileName }}</span>
						</div>
					</div>
				</div>

				<div class="upload-section">
					<div class="upload-section-header">
						<VaIcon name="layers" class="upload-section-icon" />
						<div class="upload-section-text">
							<h4 class="upload-section-title">Complemento (PDF)</h4>
							<span class="upload-section-hint">También es PDF</span>
						</div>
					</div>
					<div class="upload-section-content">
						<VaFileUpload
							v-model="complementFiles"
							dropzone
							color="warning"
							fileTypes=".pdf,application/pdf"
							uploadButtonText="Seleccionar Complemento"
							dropZoneText="Arrastra tu Complemento aquí o haz clic para seleccionar"
							class="upload-control" />
						<div v-if="complementFileName" class="selected-file">
							<VaIcon name="check_circle" color="success" size="small" />
							<span class="selected-file__name">{{ complementFileName }}</span>
						</div>
					</div>
				</div>
			</div>
		</VaCardContent>
		<VaCardActions class="footer-actions" align="right">
			<VaButton color="primary" class="btn btn-primary" @click="submitForm">
				<VaIcon name="save" class="button-icon" />
				Registrar factura
			</VaButton>
		</VaCardActions>
	</VaCard>

	<BaseModal v-model="showErrorModal" title="Error" :message="errorMessages" :testMode="false" size="medium">
		<template #body>
			<div class="error-message">
				<div class="error-header">
					<div class="error-icon">
						<i class="fas fa-exclamation-triangle"></i>
					</div>
					<h3 class="error-title">Error</h3>
				</div>
				<p class="error-description">{{ errorMessages }}</p>
			</div>
		</template>
		<template #footer>
			<button class="btn btn-primary" @click="showErrorModal = false">Entendido</button>
		</template>
	</BaseModal>
	<BaseModal v-model="showSuccessModal" title="" :message="successMessage" :test-mode="false" size="medium">
		<template #body>
			<div class="success-message">
				<div class="success-header">
					<div class="success-icon">
						<i class="fas fa-check-circle"></i>
					</div>
					<h3 class="success-title">Éxito</h3>
				</div>
				<p class="success-description">{{ successMessage }}</p>
			</div>
		</template>
		<template #footer>
			<button class="btn btn-primary" @click="showSuccessModal = false">Entendido</button>
		</template>
	</BaseModal>
</template>
<style scoped>
.registro-facturas-card {
	border-radius: 14px;
	overflow: hidden;
	box-shadow: 0 10px 30px rgba(17, 24, 39, 0.08);
}

.header-gradient {
	background: #062342;
	color: white;
}

.header-content {
	padding: 1rem 1.25rem;
}

.header-main {
	display: flex;
	align-items: center;
	gap: 0.9rem;
}

.header-icon-wrapper {
	width: 44px;
	height: 44px;
	border-radius: 12px;
	background: rgba(255, 255, 255, 0.16);
	display: flex;
	align-items: center;
	justify-content: center;
}

.header-icon {
	color: #ffffff;
}

.header-title {
	margin: 0;
	font-size: 1.25rem;
	font-weight: 900;
	letter-spacing: 0.02em;
}

.header-subtitle {
	margin: 0.2rem 0 0 0;
	opacity: 0.9;
	font-size: 0.92rem;
}

.card-content {
	padding: 1.25rem;
}

.form-grid {
	display: grid;
	grid-template-columns: 1fr auto;
	gap: 1rem;
	margin-bottom: 1.25rem;
}

.form-field {
	width: 100%;
}

.upload-grid {
	display: grid;
	grid-template-columns: repeat(3, minmax(0, 1fr));
	gap: 1rem;
}

.upload-section {
	border-radius: 14px;
	border: 1px solid rgba(148, 163, 184, 0.35);
	background: linear-gradient(180deg, rgba(248, 250, 252, 0.9) 0%, rgba(255, 255, 255, 0.9) 100%);
	padding: 1rem;
}

.upload-section-header {
	display: flex;
	align-items: center;
	gap: 0.75rem;
	margin-bottom: 0.8rem;
}

.upload-section-icon {
	opacity: 0.95;
}

.upload-section-text {
	min-width: 0;
}

.upload-section-title {
	margin: 0;
	font-weight: 900;
	font-size: 1rem;
	color: #0f172a;
}

.upload-section-hint {
	display: inline-block;
	margin-top: 0.15rem;
	font-size: 0.85rem;
	color: #64748b;
}

.upload-control {
	width: 100%;
}

.selected-file {
	margin-top: 0.75rem;
	display: flex;
	align-items: center;
	gap: 0.45rem;
	padding: 0.6rem 0.7rem;
	border-radius: 12px;
	background: rgba(16, 185, 129, 0.08);
	border: 1px solid rgba(16, 185, 129, 0.2);
}

.selected-file__name {
	color: #065f46;
	font-weight: 700;
	font-size: 0.9rem;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

@media (max-width: 1100px) {
	.upload-grid {
		grid-template-columns: 1fr;
	}
	.form-grid {
		grid-template-columns: 1fr;
	}
}

.error-description {
	font-size: 1.1rem;
	line-height: 1.6;
	margin-bottom: 1.5rem;
	padding: 1rem;
	background: rgba(255, 255, 255, 0.7);
	border-radius: 12px;
	border-left: 4px solid #f71d1d;
}
.error-message {
	background: linear-gradient(135deg, #ecbaba 0%, #e67a7a 100%);
	border: 2px solid #f71d1d;
	border-radius: 16px;
	padding: 1.5rem;
	margin: 1rem 0;
	box-shadow: 0 8px 32px rgba(254, 178, 178, 0.3);
	animation: slideInError 0.4s ease-out;
}

.error-header {
	display: flex;
	align-items: center;
	gap: 1rem;
	margin-bottom: 1.5rem;
	padding-bottom: 1rem;
	border-bottom: 2px solid #f68484;
}

.error-icon {
	width: 48px;
	height: 48px;
	background: linear-gradient(135deg, #e67a7a 0%, #e67a7a 100%);
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: 0 4px 16px rgba(245, 101, 101, 0.4);
}

.error-icon i {
	color: white;
	font-size: 1.5rem;
	animation: pulse 2s infinite;
}

.error-title {
	margin: 0;
	color: #e67a7a;
	font-size: 1.5rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.05em;
}
:deep(.dp__input) {
	background: var(--va-background-element);
	border: 1px solid var(--va-background-border);
	border-radius: 50px;
}

:deep(.dp__input)::placeholder {
	color: rgba(255, 255, 255, 0.7);
}
.success-description {
	font-size: 1.1rem;
	line-height: 1.6;
	margin-bottom: 1.5rem;
	padding: 1rem;
	background: rgba(255, 255, 255, 0.7);
	border-radius: 12px;
	border-left: 4px solid #28a745;
}
.success-message {
	background: linear-gradient(135deg, #28a745 0%, #28a745 100%);
	border: 2px solid #28a745;
	border-radius: 16px;
	padding: 1.5rem;
	margin: 1rem 0;
	box-shadow: 0 8px 32px rgba(254, 178, 178, 0.3);
	animation: slideInSuccess 0.4s ease-out;
}

.success-header {
	display: flex;
	align-items: center;
	gap: 1rem;
	margin-bottom: 1.5rem;
	padding-bottom: 1rem;
	border-bottom: 2px solid #ffffff;
}

.success-icon {
	width: 48px;
	height: 48px;
	background: linear-gradient(135deg, #28a745 0%, #28a745 100%);
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: 0 4px 16px rgba(245, 101, 101, 0.4);
}

.success-icon i {
	color: white;
	font-size: 1.5rem;
	animation: pulse 2s infinite;
}

.success-title {
	margin: 0;
	color: #ffffff;
	font-size: 1.5rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.05em;
}
.conversion-status p {
	margin: 0;
	color: #6c757d;
	font-weight: 600;
	font-size: 16px;
}
</style>
